/**
 * AdminRowActions.tsx — the icon buttons on the right of every Admin list or
 * board row, in the style of Admin > Lead Sources (the reference Admin list):
 * a ghost, extra-small, icon-only Button per action.
 *
 *   add      lucide Plus       title "Add"     (pages pass e.g. "Add product")
 *   edit     lucide Edit       title "Edit"
 *   delete   lucide Trash2     title "Delete"  red
 *   restore  lucide RotateCcw  title "Restore" (shown instead of Delete on a deleted row)
 *   clone    lucide Copy       title "Clone"
 *
 * Every button carries an accessible name (`label`, e.g. "Edit Serum") so
 * screen readers — and tests — can tell one row's Edit from another's; the
 * short `title` is the tooltip.
 *
 * On a Locked Admin page (useAdminPageLevel) every preset — add, edit, delete,
 * restore, clone — renders nothing: the change is absent, not greyed out, and
 * nothing says why (R18). A row click still opens the record (page code).
 *
 * Two traps this file exists to avoid:
 *   - An iconOnly Button renders NO children, so the icon goes in leftIcon
 *     (passing it as a child drew empty squares).
 *   - The click stays on the button: it never reaches a clickable row (which
 *     would open the edit modal) or a category header (which would toggle it).
 */
import type { ReactNode } from 'react';
import { Copy, Edit, Eye, Plus, RotateCcw, Trash2, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { useAdminPageLevel } from './AdminPageLevelContext';

/** The icon size Lead Sources uses inside its row buttons. */
export const ROW_ACTION_ICON_CLASS = 'h-3.5 w-3.5';
/** Lead Sources' red Delete button. */
export const ROW_ACTION_DANGER_CLASS = 'text-red-500 hover:text-red-600 hover:bg-red-50';

export interface RowActionButtonProps {
  /** Accessible name, e.g. "Edit Serum". */
  label: string;
  /** Tooltip, e.g. "Edit". */
  title: string;
  icon: ReactNode;
  onClick: () => void;
  /** Red, like Lead Sources' Delete. */
  danger?: boolean;
  disabled?: boolean;
  className?: string;
}

/** One row action. Prefer AdminRowAction, which picks the icon and title for you. */
export function RowActionButton({ label, title, icon, onClick, danger = false, disabled, className }: RowActionButtonProps) {
  return (
    <Button
      variant="ghost"
      size="xs"
      iconOnly
      aria-label={label}
      title={title}
      leftIcon={icon}
      disabled={disabled}
      className={cn(danger && ROW_ACTION_DANGER_CLASS, className)}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    />
  );
}

export type RowActionKind = 'add' | 'edit' | 'delete' | 'restore' | 'clone';

const PRESETS: Record<RowActionKind, { Icon: LucideIcon; title: string; danger?: boolean }> = {
  add: { Icon: Plus, title: 'Add' },
  edit: { Icon: Edit, title: 'Edit' },
  delete: { Icon: Trash2, title: 'Delete', danger: true },
  restore: { Icon: RotateCcw, title: 'Restore' },
  clone: { Icon: Copy, title: 'Clone' },
};

export interface AdminRowActionProps {
  kind: RowActionKind;
  /** Accessible name, e.g. "Delete Serum". */
  label: string;
  onClick: () => void;
  /** Overrides the preset tooltip, e.g. "Add product" for an add action. */
  title?: string;
  disabled?: boolean;
  /**
   * Why the action can't be done (e.g. a product type still in use). The icon
   * is greyed out like corp's — no colour, no hover — and the reason is its
   * tooltip; the click still runs so the page can say why (as corp does).
   */
  blockedReason?: string | null;
  /**
   * For an `edit` action on a row whose click does not open its record (a
   * category or group row, where a click expands it). On a Locked page the
   * action stays as a plain View icon that opens the same, read-only, modal,
   * so the record can still be read (R8). Without it, a Locked page shows
   * nothing.
   */
  viewWhenLocked?: boolean;
}

/** A preset row action: the Lead Sources icon, tooltip and colour for its kind. Nothing on a Locked page, except `viewWhenLocked`. */
export function AdminRowAction({ kind, label, onClick, title, disabled, blockedReason, viewWhenLocked }: AdminRowActionProps) {
  const locked = useAdminPageLevel() === 'locked';
  if (locked) {
    if (kind !== 'edit' || !viewWhenLocked) return null;
    return (
      <RowActionButton
        label={label.replace(/^Edit\b/, 'View')}
        title="View"
        icon={<Eye className={ROW_ACTION_ICON_CLASS} />}
        onClick={onClick}
      />
    );
  }
  const preset = PRESETS[kind];
  const Icon = preset.Icon;
  const blocked = Boolean(blockedReason);
  return (
    <RowActionButton
      label={label}
      title={blocked ? (blockedReason as string) : (title ?? preset.title)}
      icon={<Icon className={ROW_ACTION_ICON_CLASS} />}
      danger={preset.danger && !blocked}
      disabled={disabled}
      className={blocked ? ROW_ACTION_BLOCKED_CLASS : undefined}
      onClick={onClick}
    />
  );
}

/** Corp's greyed-out action: dimmed, no colour, no hover. */
export const ROW_ACTION_BLOCKED_CLASS = 'opacity-40 text-slate-400 hover:bg-transparent hover:text-slate-400 cursor-not-allowed';

/** Lead Sources' actions cell: the buttons right-aligned, a small gap apart. */
export function AdminRowActions({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex justify-end gap-1', className)}>{children}</div>;
}
